import React, { useState, useEffect } from 'react';
import { Member, MembersView } from '@/models/Member';
import { MemberDetails } from '@/models/MemberDetails';
import { plainToClass } from '@/models/transform';
import { useTranslation } from 'react-i18next';
import { InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import { MembersService } from '@/services/domain/MembersService';
import { useToastMessages } from '@/hooks/useToastMessages';
import { useAuth } from '@/components/providers/AuthProvider';
import { ButtonWrapper } from '@/components/ui/ButtonWrapper';
import { Button, ButtonText } from '@/components/gluestack/button';
import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogCloseButton,
  AlertDialogBody,
  AlertDialogFooter,
} from '@/components/gluestack/alert-dialog';
import { Heading } from '@/components/gluestack/heading';
import { Text } from '@/components/gluestack/text';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { Cancel01Icon, StarIcon as StarSolidIcon } from '@hugeicons-pro/core-solid-rounded';
import { StarIcon as StarStrokeIcon } from '@hugeicons-pro/core-stroke-rounded';

interface FollowButtonProps {
  member: Member;
  className?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  hideIcon?: boolean;
}

export function FollowButton({ member, className, size = 'sm', hideIcon = false }: FollowButtonProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showSuccessToast, showErrorToast } = useToastMessages();
  const [isFollowing, setIsFollowing] = useState(member?.IsFollowing ?? false);
  const [showUnfollowConfirmation, setShowUnfollowConfirmation] = useState(false);

  const { accountStatus } = useAuth();
  const currentMemberId = accountStatus?.MemberId;

  // Update local state if the prop changes
  useEffect(() => {
    if (member) {
      setIsFollowing(member.IsFollowing);
    }
  }, [member?.IsFollowing, member?.Id]);

  // Use a generic query key for updating follow status across different member lists
  const genericMembersQueryKey = ['members'];

  // Function to update the cache optimistically across all member lists and details
  const updateMembersCache = (targetMemberId: number, newFollowingState: boolean) => {
    // Update member lists caches with query keys starting with ['members']
    queryClient.setQueriesData(
      { queryKey: genericMembersQueryKey, exact: false },
      (oldData: InfiniteData<MembersView> | undefined) => {
        if (!oldData || !oldData.pages) return oldData;

        let memberFound = false;
        const newPages = oldData.pages.map((page: MembersView) => {
          // Check if MembersBasic exists and is an array
          if (!page?.MembersBasic || !Array.isArray(page.MembersBasic)) {
            return page;
          }

          const memberIndex = page.MembersBasic.findIndex((m) => m.Id === targetMemberId);
          if (memberIndex !== -1) {
            memberFound = true;
            // Create a new array with the updated member to avoid direct mutation
            const updatedMembers = [...page.MembersBasic];
            // Use plainToClass to create a new instance with the updated property while preserving getters
            const updatedMember = plainToClass(Member, { ...updatedMembers[memberIndex], IsFollowing: newFollowingState });
            updatedMembers[memberIndex] = updatedMember;
            return { ...page, MembersBasic: updatedMembers };
          }
          return page;
        });

        // Only return new data structure if the member was found and updated
        return memberFound ? { ...oldData, pages: newPages } : oldData;
      }
    );

    // Update member-details caches (individual member profile data)
    queryClient.setQueriesData(
      { queryKey: ['member-details'], exact: false },
      (oldData: MemberDetails | undefined) => {
        if (!oldData || oldData.Id !== targetMemberId) return oldData;
        
        // Update the IsFollowing property using plainToClass to preserve prototype chain (getters like PlaceNameCleaned)
        return plainToClass(MemberDetails, { ...oldData, IsFollowing: newFollowingState });
      }
    );
  };

  const followMutation = useMutation({
    mutationFn: () => MembersService.followMember(member.Id),
    onMutate: async () => {
      // Optimistically update the local state
      setIsFollowing(true);
      // Optimistically update relevant query caches
      const previousDataMap = new Map();
      
      // Store previous data for member lists
      queryClient
        .getQueryCache()
        .findAll({ queryKey: genericMembersQueryKey, exact: false })
        .forEach((query) => {
          previousDataMap.set(query.queryKey, query.state.data);
        });
      
      // Store previous data for member details
      queryClient
        .getQueryCache()
        .findAll({ queryKey: ['member-details'], exact: false })
        .forEach((query) => {
          previousDataMap.set(query.queryKey, query.state.data);
        });
      
      updateMembersCache(member.Id, true);
      return { previousDataMap };
    },
    onSuccess: () => {
      showSuccessToast(t('member.follow.success', { userName: member.UserName }));
      // Invalidate follower/following lists to ensure MemberListSheet shows updated data
      queryClient.invalidateQueries({ queryKey: ['members', 'followers', member.Id] });
      if (currentMemberId) {
        queryClient.invalidateQueries({ queryKey: ['members', 'following', currentMemberId] });
      }
    },
    onError: (error, _variables, context) => {
      // Revert optimistic updates on error
      setIsFollowing(false);
      context?.previousDataMap.forEach((data, key) => {
        queryClient.setQueryData(key, data);
      });
      showErrorToast(t('member.follow.error'), error.message);
    },
  });

  const unfollowMutation = useMutation({
    mutationFn: () => MembersService.unfollowMember(member.Id),
    onMutate: async () => {
      // Optimistically update the local state
      setIsFollowing(false);
      // Optimistically update relevant query caches
      const previousDataMap = new Map();
      
      // Store previous data for member lists
      queryClient
        .getQueryCache()
        .findAll({ queryKey: genericMembersQueryKey, exact: false })
        .forEach((query) => {
          previousDataMap.set(query.queryKey, query.state.data);
        });
      
      // Store previous data for member details
      queryClient
        .getQueryCache()
        .findAll({ queryKey: ['member-details'], exact: false })
        .forEach((query) => {
          previousDataMap.set(query.queryKey, query.state.data);
        });
      
      updateMembersCache(member.Id, false);
      return { previousDataMap };
    },
    onSuccess: () => {
      showSuccessToast(t('member.unfollow.success', { userName: member.UserName }));
      // Invalidate follower/following lists to ensure MemberListSheet shows updated data
      queryClient.invalidateQueries({ queryKey: ['members', 'followers', member.Id] });
      if (currentMemberId) {
        queryClient.invalidateQueries({ queryKey: ['members', 'following', currentMemberId] });
      }
    },
    onError: (error, _variables, context) => {
      // Revert optimistic updates on error
      setIsFollowing(true);
      context?.previousDataMap.forEach((data, key) => {
        queryClient.setQueryData(key, data);
      });
      showErrorToast(t('member.unfollow.error'), error.message);
    },
  });

  const handleFollow = () => {
    if (isMutationPending) return;
    followMutation.mutate();
  };

  const handleUnfollowConfirm = () => {
    if (isMutationPending) return;
    unfollowMutation.mutate();
    setShowUnfollowConfirmation(false);
  };

  const handleFollowToggle = () => {
    if (isMutationPending) return;
    if (isFollowing) {
      setShowUnfollowConfirmation(true);
    } else {
      handleFollow();
    }
  };

  const isMutationPending = followMutation.isPending || unfollowMutation.isPending;

  // Don't render if this is the current user (check after all hooks to avoid hook rule violations)
  if (member.Id === currentMemberId) {
    return null;
  }

  return (
    <>
      <ButtonWrapper
        icon={hideIcon ? undefined : (isFollowing ? StarSolidIcon : StarStrokeIcon)}
        text={isFollowing ? t('common.followed') : t('common.follow')}
        size={size}
        action={isFollowing ? 'neutral' : 'primary'}
        variant="solid"
        iconClassName={isFollowing ? 'color-yellow-500' : ''}
        iconSize={18}
        className={className}
        onPress={handleFollowToggle}
        isDisabled={isMutationPending}
      />

      {/* Unfollow Confirmation Dialog */}
      <AlertDialog
        isOpen={showUnfollowConfirmation}
        onClose={() => setShowUnfollowConfirmation(false)}
        size="md">
        <AlertDialogBackdrop />
        <AlertDialogContent>
          <AlertDialogHeader>
            <Heading size="md">{t('member.unfollow.confirmTitle')}</Heading>
            <AlertDialogCloseButton>
              <HugeIconsWrapper icon={Cancel01Icon} size={20} className="color-neutral-500" />
            </AlertDialogCloseButton>
          </AlertDialogHeader>
          <AlertDialogBody className="mt-3">
            <Text>{t('member.unfollow.confirmMessage', { userName: member.UserName })}</Text>
          </AlertDialogBody>
          <AlertDialogFooter className="mt-6 gap-3">
            <Button
              variant="outline"
              action="neutral"
              onPress={() => setShowUnfollowConfirmation(false)}
              disabled={isMutationPending}>
              <ButtonText>{t('common.cancel')}</ButtonText>
            </Button>
            <Button action="negative" onPress={handleUnfollowConfirm} disabled={isMutationPending}>
              <ButtonText>{t('common.continue')}</ButtonText>
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}